import { Spinner } from "@/components/Loader/Spinner";
import { Pressable } from "@/components/Theme/Pressable";
import { Text } from "@/components/Theme/Text";
import { View } from "@/components/Theme/View";
import { text } from '@/constants/Styles';
import { iconSize } from '@/helper/Sizes';
import { useLoaderBackground } from "@/hooks/useLoaderBackground";
import { useThemeColor } from '@/hooks/useThemeColor';
import isEqual from "lodash/isEqual";
import { memo, useMemo } from 'react';
import { StyleSheet } from 'react-native';

const context = require.context('../../assets/icons', false, /\.svg$/);

export const IconButton = (props) => {

    const { 
        icon = null, 
        size = 24,
        width = null, 
        height = null,
        color = {
            light: 'muted.800', 
            dark: 'muted.50'
        },
        backgroundColor = 'transparent',
        style = {},
        onPress = null,
        onLongPress = null,
        onPressIn = null,
        onPressOut = null,
        disabled = false,
        rounded = false,
        variant = null,
        label = null,
        labelProps = {},
        hoverTitle = null,
        loading = false,
        ...rest
    } = props;

    if (icon == null)
        throw new Error('Icon component requires an icon prop.');

    const IconSVGComponent = context(`./${icon}.svg`)?.default;

    const externalStyleReduced = useMemo(() => (
        {...(Array.isArray(style) ? style.reduce((acc, style) => {
            return {...acc, ...style}
        }, {}) : style)}
    ), [style]); 

    const { backgroundColor:loaderBackgroundColor } = useLoaderBackground();
    const _iconColor = useThemeColor(color, 'test');
    const _backgroundColor = useThemeColor(backgroundColor, 'background');

    const _style = {
        iconWidth: iconSize(width ?? size), 
        iconHeight: iconSize(height ?? size),
        borderRadius: rounded ? 100 : undefined,
        ...externalStyleReduced,
    };

    const variantStyle = useMemo(() => {
        if (variant == 'outline') {
            return {
                borderWidth: 1,
                borderColor: color,
                paddingHorizontal: 5,
                paddingVertical: 3
            }
        }
    }, [variant, color]);
    
	if (typeof IconSVGComponent !== 'function') {
        console.log(`Icon component for icon "${icon}" not found.`);
        return null;
    }
	
    const isPressable = onPress != null || onLongPress != null || onPressIn != null || onPressOut != null;
    const Component = isPressable ? Pressable : View
    
    return (
        <Component 
            style={[styles.container, _style, variantStyle, {backgroundColor: _backgroundColor}]}
            onPress={onPress}
            onLongPress={onLongPress}
            onPressIn={onPressIn}
            onPressOut={onPressOut}
            disabled={disabled}
            hoverTitle={hoverTitle}
            {...rest}
            noSwitch
            hitSlop={25}
            >
            {loading &&
                <View bgColor={loaderBackgroundColor} zIndex={50} position="absolute" left={0} right={0} top={0} bottom={0} alignItems="center" justifyContent="center" borderRadius={rest?.borderRadius ?? 9999}>
                    <Spinner bold size={iconSize(width ?? size)} color={_iconColor} />
                </View>   
            }

            {IconSVGComponent({fill: _iconColor, width: _style.iconWidth, height: _style.iconHeight})}
            {label != null && <Text  style={[text.small, labelProps?.style]} id={label} {...labelProps} />}
        </Component>
    );

};

const styles = StyleSheet.create({
    container: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 10
    }
});